/* ==========================================================================
   Aurmina - Tier savings
   REVISION e
   Revision:   e - 30 Aug 2026
   Goes in:    assets/aurmina-tier-savings.js
   Used by:    sections/aurmina-pricing-tiers.liquid rev c
               sections/aurmina-product-grid.liquid rev a
               blocks/aurmina-price-detail.liquid rev c
               blocks/aurmina-well-overlay.liquid rev c

   REV D, two changes, both additive. A tier carrying neither new attribute
   behaves exactly as it did at rev c, which is what keeps the two installed
   collection grids working untouched.

   1. data-aurmina-scope - THE PRICE NO LONGER HAS TO BE INSIDE THE TIER.

      Rev c required [ref="priceContainer"] to be a DESCENDANT of the tier
      element. True in a product card, where one card holds everything.
      Not true on a product page: Atelier renders the price in its own
      `price` block, a SIBLING of ours in the same column. Rev c would have
      found no container, returned early, and left the server-rendered
      retail figures in place for wholesale customers - silently, which is
      the failure mode project section 5 exists to prevent.

      data-aurmina-scope names an ancestor to search instead. The product
      blocks set it to .product-information__grid, the same element
      snippets/samita-custom.liquid declares as its product context, so the
      script and the wholesale app agree by construction.

      The MutationObserver follows the same resolution, so a Samita rewrite
      on a sibling price still triggers a recalculation.

   2. data-save-decimals="auto" - ONE ROUNDING RULE FOR EVERY PRODUCT.

      Rev c offered whole percents or a per-card "1". Neither reproduces the
      five real figures on this store, which are 22.219, 33.330, 9.991,
      12.502 and 15.002 and are published as 22, 33, 10, 12.5 and 15.

      The rule: round to a whole percent, UNLESS the value sits within 0.05
      of a half-integer, in which case print one decimal. Only the
      Regenerative Farming Starter Kit qualifies. Approved 30 Aug as the
      single rule, replacing the per-card setting.

      Worth stating plainly because it looks arbitrary: a figure landing on
      an exact half is a deliberate merchandising number and should survive;
      anything else is arithmetic noise from a price ending in .99 and
      should round. 0.05 is the tolerance that separates the two.

      "1" still forces one decimal and absent still means whole, so nothing
      already placed changes.


   REV E, one change. THE LABEL LOST ITS TRAILING SPACE.

   Rev d wrote prefix + figure straight out, so a label of "Save " produced
   "Save 22%" and a label of "Save" produced "Save22%". Shopify strips
   trailing whitespace when it stores a text setting, so the shipped default
   of "Save " came back as "Save" and every badge rendered without the space.

   The prefix is now trimmed and a single space appended, which cannot be
   lost, and a blank label still gives a bare percentage with no leading gap.
   Matches the same fix in the two block files at rev c.

   WHY THIS EXISTS

   Sami B2B Wholesale rewrites displayed prices client-side for tagged
   customers. Anything Liquid computed from compare_at_price is therefore the
   RETAIL figure, and wrong for a logged-in wholesale customer: they would see
   their own price beside a saving that is not theirs.

   This reads the price back off the page after Samita has finished and
   recomputes the savings badge and the per-unit figure from what is actually
   rendered. The server-side values are printed first, so the card is correct
   without JavaScript and correct for retail customers immediately. This only
   ever corrects a number that is already there.

   Vanilla, no dependencies, no build step, per project section 8.

   MARKUP CONTRACT
     [data-aurmina-tier]              a tier card, optional data-units
     data-aurmina-scope               ancestor selector to find the price in,
                                      optional; omit when the price container
                                      is inside the tier
     [ref="priceContainer"] .price    the live price (Samita's own hook)
     .compare-at-price                the struck-through price, if shown
     [data-aurmina-save]              badge(s), possibly a hidden spacer
     [data-aurmina-per]               the per-unit figure
     data-save-prefix                 label before the figure, optional
     data-save-decimals="1"           one decimal place, optional
     data-save-decimals="auto"        whole, or one decimal on a half, optional

   Re-runs on shopify:section:load so sections re-rendered in the theme
   editor are recalculated too.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__aurminaTierSavings) return;
  window.__aurminaTierSavings = true;

  var SETTLE_MS = 400;
  var TIE_TOLERANCE = 0.05;

  /* Reads a rendered money string into an integer number of minor units.
     Deliberately format-agnostic: it keeps digits and separators, then treats
     the LAST separator followed by exactly two digits as the decimal point,
     which is true for both 1.234,56 and 1,234.56. Returns null rather than
     guessing when there is nothing numeric to read. */
  function parseMoney(text) {
    if (!text) return null;
    var raw = String(text).replace(/[^0-9.,]/g, '');
    if (!raw) return null;

    var decimal = raw.match(/[.,](\d{2})$/);
    var units;
    var cents = 0;

    if (decimal) {
      units = raw.slice(0, raw.length - 3).replace(/[.,]/g, '');
      cents = parseInt(decimal[1], 10);
    } else {
      units = raw.replace(/[.,]/g, '');
    }

    if (!units) units = '0';
    var value = parseInt(units, 10) * 100 + cents;
    return isNaN(value) ? null : value;
  }

  /* Formats minor units back out using the shape of the price already on the
     page, so currency symbol, position and separators are inherited rather
     than assumed. */
  function formatLike(sample, amount) {
    var major = Math.floor(amount / 100);
    var minor = amount % 100;
    var body = String(major);

    var grouped = sample.match(/\d[.,]\d{3}/);
    if (grouped) {
      var sep = grouped[0].charAt(1);
      body = body.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    }

    var decimal = sample.match(/[.,](\d{2})(?!\d)/);
    if (decimal) {
      body += decimal[0].charAt(0) + (minor < 10 ? '0' + minor : String(minor));
    }

    var prefix = sample.match(/^[^\d]*/);
    var suffix = sample.match(/[^\d]*$/);
    return (prefix ? prefix[0] : '') + body + (suffix ? suffix[0] : '');
  }

  /* Finds the price container for a tier. Normally it is inside the tier.
     When data-aurmina-scope is set the tier and the price are siblings, so
     resolve upward to the named ancestor and search there instead. Falls
     back to the descendant lookup if the ancestor is not found, so a bad
     selector degrades to rev c behaviour rather than throwing. */
  function findContainer(tier) {
    var scope = tier.getAttribute('data-aurmina-scope');
    if (scope) {
      var root = tier.closest ? tier.closest(scope) : null;
      if (root) {
        var scoped = root.querySelector('[ref="priceContainer"]');
        if (scoped) return scoped;
      }
    }
    return tier.querySelector('[ref="priceContainer"]');
  }

  /* Rounds a percentage by the one project rule. 'auto' prints a whole
     percent unless the value sits within TIE_TOLERANCE of a half-integer,
     in which case it prints one decimal. See the rev d header. */
  function formatPercent(exact, mode) {
    if (mode === '1') {
      return String(Math.round(exact * 10) / 10);
    }

    if (mode === 'auto') {
      var nearestHalf = Math.round(exact * 2) / 2;
      var isHalf = Math.abs(nearestHalf * 2) % 2 === 1;
      if (isHalf && Math.abs(exact - nearestHalf) <= TIE_TOLERANCE) {
        return String(nearestHalf);
      }
    }

    return String(Math.round(exact));
  }

  function updateTier(tier) {
    var container = findContainer(tier);
    if (!container) return;

    var priceEl = container.querySelector('.price');
    if (!priceEl) return;

    var price = parseMoney(priceEl.textContent);
    if (price === null || price <= 0) return;

    /* Savings badges, recomputed against whatever compare-at is showing now.
       A card may carry more than one - the product grid prints the figure on
       the price line and, optionally, on the flag over the image. */
    var badges = tier.querySelectorAll('[data-aurmina-save]');
    if (badges.length) {
      var compareEl = container.querySelector('.compare-at-price');
      var compare = compareEl ? parseMoney(compareEl.textContent) : null;

      if (compare !== null && compare > price) {
        var prefix = tier.getAttribute('data-save-prefix');
        if (prefix === null) prefix = 'Save';
        prefix = prefix.replace(/\s+$/, '');
        if (prefix) prefix += ' ';

        var exact = ((compare - price) / compare) * 100;
        var figure = formatPercent(exact, tier.getAttribute('data-save-decimals'));

        for (var b = 0; b < badges.length; b++) {
          badges[b].textContent = prefix + figure + '%';
          badges[b].classList.remove('aurmina-tiers__save--spacer');
          badges[b].classList.remove('aurmina-pgrid__save--spacer');
          badges[b].removeAttribute('aria-hidden');
        }
      }
      /* No compare-at means no saving to state. Server-rendered badges are
         left alone rather than cleared, so a card never loses a correct badge
         because the markup changed shape. */
    }

    /* Per-unit figure. */
    var per = tier.querySelector('[data-aurmina-per]');
    var units = parseInt(tier.getAttribute('data-units'), 10);
    if (per && units > 0) {
      per.textContent = formatLike(priceEl.textContent.trim(), Math.round(price / units));
    }
  }

  function run(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var tiers = scope.querySelectorAll('[data-aurmina-tier]');
    for (var i = 0; i < tiers.length; i++) updateTier(tiers[i]);
  }

  /* Samita rewrites prices after its own script runs, and does not announce
     when it has finished. Rather than racing it, watch the price containers
     and recalculate when one changes, with a short settle delay so a burst of
     mutations produces one update. */
  function observe(root) {
    if (typeof MutationObserver !== 'function') return;

    var timer = null;
    var observer = new MutationObserver(function () {
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        run(root);
      }, 120);
    });

    /* Observe the container each tier actually reads, which is not always a
       descendant of the tier now that data-aurmina-scope exists. A Set would
       be tidier; a seen-array keeps this ES5 and dependency-free. */
    var tiers = (root || document).querySelectorAll('[data-aurmina-tier]');
    var seen = [];

    for (var i = 0; i < tiers.length; i++) {
      var container = findContainer(tiers[i]);
      if (!container) continue;
      if (seen.indexOf(container) !== -1) continue;
      seen.push(container);
      observer.observe(container, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }
  }

  function init(root) {
    run(root);
    setTimeout(function () {
      run(root);
    }, SETTLE_MS);
    observe(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  /* Theme editor: sections re-render without a page reload. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });
})();
